import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";

function RightVideoPreview({
  setLoading,
  network,
  setCallFinalLogApi,
  previewFlag,
  currentUserVideoRef,
  currentVideoStream,
  currentAudioStream,
  concludeDemo,
  monitorFlag,
  previewConfig,
}) {
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const toggleMic = () => {
    setIsMuted((prevState) => {
      const newMutedState = !prevState;
      if (currentUserVideoRef.current) {
        currentUserVideoRef.current.muted = newMutedState;
      }
      if (currentAudioStream && currentAudioStream.getAudioTracks) {
        currentAudioStream.getAudioTracks().forEach((track) => {
          track.enabled = !newMutedState;
        });
      }
      return newMutedState;
    });
  };

  const toggleCamera = () => {
    setIsCameraOff((prevState) => {
      const newCameraState = !prevState;
      if (
        currentUserVideoRef.current &&
        currentUserVideoRef.current.srcObject
      ) {
        // Get all video tracks from the media stream and enable/disable them
        const videoTracks =
          currentUserVideoRef.current.srcObject.getVideoTracks();
        videoTracks.forEach((track) => (track.enabled = !newCameraState));
      }
      return newCameraState;
    });
  };

  useEffect(() => {
    if (
      !isCameraOff &&
      !isMuted &&
      errorMessage &&
      errorMessage.includes("turn on")
    ) {
      setErrorMessage("");
    }
  }, [isCameraOff, isMuted, errorMessage]);

  const startInterviewHandler = () => {
    //if video stream is required, check disable and brightness
    if (currentVideoStream) {
      const videoTrack = currentVideoStream?.getVideoTracks()?.[0];
      if (
        isCameraOff ||
        isMuted ||
        videoTrack?.readyState !== "live" ||
        !videoTrack?.enabled
      ) {
        setErrorMessage("Please turn on your camera and mic to proceed!");
        return;
      }

      // const isBright = instantBrightnessCheck();
      // if (!isBright) {
      //   return;
      // }
    }
    if (errorMessage && errorMessage.includes("turn on")) {
      return;
    }

    setErrorMessage("");
    setCallFinalLogApi(true);
  };

  useEffect(() => {
    if (!currentVideoStream || isCameraOff) return; // Only run when camera is on and stream is available

    const video = document.querySelector(".main-video");
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const brightnessThreshold = 30;
    let brightnessInterval;

    const handleLoadedMetadata = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    };

    const startBrightnessCheck = () => {
      brightnessInterval = setInterval(() => {
        if (isVideoTooDark()) {
          setErrorMessage(
            "Background is too dark or the webcam is covered. Please uncover the webcam or move to a place with better lighting.",
          );
        } else {
          setErrorMessage((prev) => {
            if (prev.includes("turn on")) {
              return prev;
            } else return "";
          });
        }
      }, 1500);
    };

    const isVideoTooDark = () => {
      if (canvas.width === 0 || canvas.height === 0) {
        console.error("Canvas dimensions are zero.");
        return false;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let totalBrightness = 0;

      for (let i = 0; i < data.length; i += 4) {
        const brightness =
          0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        totalBrightness += brightness;
      }

      const avgBrightness = totalBrightness / (data.length / 4);
      return avgBrightness < brightnessThreshold;
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    if (!isCameraOff) {
      startBrightnessCheck();
    }

    return () => {
      clearInterval(brightnessInterval);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [currentVideoStream, isCameraOff]);

  return (
    <div className="">
      <p className="mb-1 text-center font-semibold text-[1.05rem] text-[#101828]">
        Camera Preview
      </p>
      <div className="preview-modal rounded-lg shadow-lg bg-gradient-to-b from-gray-200 via-gray-300 to-gray-500 h-80 w-full md:w-[65%] xl:w-[85%] m-auto">
        <video
          className={`${
            errorMessage
              ? errorMessage.includes("turn on")
                ? "outline outline-[3px] outline-red-600"
                : "outline outline-[3px] outline-amber-500"
              : ""
          } main-video h-full w-full object-cover object-center rounded-lg`}
          ref={currentUserVideoRef}
          autoPlay
          loop
          muted
        />
      </div>
      {errorMessage && (
        <div
          className={`text-center text-[0.85rem] font-medium ${
            errorMessage.includes("turn on") ? "text-red-500" : "text-amber-600"
          } mt-[0.3rem] mb-[-.35rem] w-[95%] md:w-[60%] xl:w-[80%] m-auto leading-[1.05rem]`}
        >
          {errorMessage}
        </div>
      )}
      <div className="flex gap-[0.6rem] my-3 justify-center preview-controller">
        <Button
          type={isCameraOff ? "primary" : "default"}
          danger={isCameraOff}
          icon={
            isCameraOff ? (
              <VideoOff size={20} style={{ color: "white" }} />
            ) : (
              <Video size={20} />
            )
          }
          onClick={toggleCamera}
          disabled={!currentVideoStream}
        />
        <Button
          type={isMuted ? "primary" : "default"}
          danger={isMuted}
          icon={
            isMuted ? (
              <MicOff size={20} style={{ color: "white" }} />
            ) : (
              <Mic size={20} />
            )
          }
          onClick={toggleMic}
          disabled={!currentVideoStream}
        />
      </div>
      <div className="text-center mt-[0.7rem]">
        {!previewConfig?.monitoringToolEnabled && (
          <button
            className={`${
              previewFlag && network && concludeDemo
                ? "btn-blue"
                : "btn-grey-disable"
            } px-[2.6rem] py-[0.4rem] cursor-pointer`}
            disabled={!previewFlag || !network || !concludeDemo}
            onClick={startInterviewHandler}
          >
            Join Assessment
          </button>
        )}
      </div>
    </div>
  );
}

export default RightVideoPreview;
